import {getCookieValue, setRefreshTokenCookie} from "./functionFn";

let defaultLogoutUrl;
let defaultRefreshTokenApiUrl;
let defaultAxiosSource;

if (typeof window !== 'undefined' && window && (window.location.hostname.includes('demo') || window.location.hostname.includes('test') || window.location.hostname.includes('localhost'))) {
    defaultLogoutUrl = 'https://login.fixit-demo.no';
    defaultRefreshTokenApiUrl = 'https://fixitcoreapi.fixit-demo.no/api/v2/Authorization/refresh-token';
    defaultAxiosSource = 'https://cdn.fixit-demo.no/external/axios/0.27.2/axios.min.js';

    // if (window.location.href.includes('localhost')) {
    //     defaultRefreshTokenApiUrl = 'http://localhost:43503/api/v2/Authorization/refresh-token'; //For localhost testing
    // }

    if(window.location.hostname.includes('test')){
        defaultLogoutUrl = 'https://login.test.odindata.no';
        defaultRefreshTokenApiUrl = 'https://fixitcoreapi.test.odindata.no/api/v2/Authorization/refresh-token';
    }

} else {
    defaultLogoutUrl = 'https://login.fixitonline.no';
    defaultRefreshTokenApiUrl = 'https://fixitcoreapi.fixitonline.no/api/v2/Authorization/refresh-token';
    defaultAxiosSource = 'https://cdn.fixitonline.no/external/axios/0.27.2/axios.min.js'
}

/**
 * Loads axios in the window object to be used as window.axios, used when there's no existing axios instance in application, or existing instance is already loaded in the window object.
 * Automatically sets axios interceptors for authorization and authentication.
 * @param {string} logoutUrl - optional, api endpoint to log out the current user, pass "noredirect" {string} if you don't need the logout redirection.
 * @param {string} refreshTokenApiUrl - optional, refresh token api endpoint
 * @param {string} axiosSource - optional, cdn source for the axios script, if the internal fixitcdn ones aren't enough
 * @returns {Promise<unknown>}
 */

//To be used in legacy projects
export async function axiosLoader(logoutUrl = defaultLogoutUrl, refreshTokenApiUrl = defaultRefreshTokenApiUrl, axiosSource = defaultAxiosSource) {
    if (typeof window !== 'undefined' && window && window.axios) {
        setAxiosInterceptors(logoutUrl, refreshTokenApiUrl);
    } else {
        return new Promise((resolve) => {
            let script = document.createElement("script");
            script.onload = () => {
                resolve();

                if (window.axios) {
                    setAxiosInterceptors(logoutUrl, refreshTokenApiUrl);
                }
            };
            script.async = true;

            //Latest specific version on 13. June 2022., to prevent apps breaking on future axios updates
            script.src = axiosSource;
            document.head.appendChild(script);
        });
    }

}

/**
 * Uses existing axios instance that's been passed to it to set axios interceptors for authorization and authentication.
 * @param {Object} axios - axios instance to set authorization and authentication interceptors on
 * @param {string} logoutUrl - optional, api endpoint to log out the current user, pass "noredirect" {string} if you don't need the logout redirection
 * @param {string} refreshTokenApiUrl - optional, refresh token api endpoint
 * @returns {Promise<unknown>}
 */

//To be used with Vue components & Vue projects
export function axiosRefreshTokenSetup(axios, logoutUrl = defaultLogoutUrl, refreshTokenApiUrl = defaultRefreshTokenApiUrl) {
    let isFetchingRefreshToken = false;
    let failedRequestsQueue = [];

    const processQueue = (error, token = null) => {
        for (const promise of failedRequestsQueue) {
            if (error)
                promise.reject(error);
            else
                promise.resolve(token);
        }
        failedRequestsQueue = [];

    };

    if(!axios.interceptors.response.isRefreshTokenInterceptorSet){
        axios.interceptors.response.use(response => {
            return response;
        }, err => {
            const originalRequest = err.config;
            /**
             * If the connection has been timed out or aborted, notify from the console.
             */
            if (err.code === "ECONNABORTED") {
                console.warn('Connection aborted, call failed at: ' + err.config.url)
            }
            /*
             * If we get 401 response code, JWT token is expired, and we needed to fetch new token with a refresh token which is saved in browser cookies.
             */
            else if (err.response && err.response.status === 401 && !originalRequest._retry) {
                // if any requests get 401 while refresh-token request is pending, put them in the process queue
                if (isFetchingRefreshToken) {
                    return new Promise(function (resolve, reject) {
                        failedRequestsQueue.push({resolve, reject});
                    }).then(token => {
                        originalRequest.headers["Authorization"] = "Bearer " + token;
                        return axios(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }
    
                originalRequest._retry = true;
                isFetchingRefreshToken = true;
                /*
                * We have to set config.withCredentials = true in order to send browser cookies within refresh-token request to FixitCoreApi which is on a different domain.
                * This setting can be set globally on every axios request - if needed.
                */
                return new Promise(function (resolve, reject) {
                    axios.post(refreshTokenApiUrl, null, {withCredentials: true}).then(({data}) => {
                        axios.defaults.headers.common["Authorization"] = "Bearer " + (data.jwtToken || data.accessToken || data.access_token);
                        originalRequest.headers["Authorization"] = "Bearer " + (data.jwtToken || data.accessToken || data.access_token);
                        processQueue(null, (data.jwtToken || data.accessToken || data.access_token));
                        resolve(axios(originalRequest));
                    }).catch(err => {
                        processQueue(err, null);
                        /*
                        * If refresh token is expired redirect to login page
                        */
                        if (logoutUrl !== "noredirect") {
                            window.location.href = logoutUrl + "?returnUrl=" + window.location.href;
                        }
    
                        reject(err);
                    }).then(() => {
                        isFetchingRefreshToken = false;
                    });
                });
            }
    
            return Promise.reject(err);
        });

        
        axios.interceptors.response.isRefreshTokenInterceptorSet = true;
    }

    
    return true;
}

function setAxiosInterceptors(logoutUrl = defaultLogoutUrl, refreshTokenApiUrl = defaultRefreshTokenApiUrl) {
    let isFetchingRefreshToken = false;
    let failedRequestsQueue = [];

    const processQueue = (error, token = null) => {
        for (const promise of failedRequestsQueue) {
            if (error) promise.reject(error);
            else promise.resolve(token);
        }
        failedRequestsQueue = [];
    };

    if(!window.axios.interceptors.response.isRefreshTokenInterceptorSet){
        window.axios.interceptors.response.use(
            (response) => {
                return response;
            },
            (err) => {
                const originalRequest = err.config;
    
                /**
                 * If the connection has been timed out or aborted, notify from the console.
                 */
                if (err.code === "ECONNABORTED") {
                    console.warn('Connection aborted, call failed at: ' + err.config.url)
                }
                /*
                 * If we get 401 response code, JWT token is expired, and we needed to fetch new token with a refresh token which is saved in browser cookies.
                 */
                else if (err.response && err.response.status === 401 && !originalRequest._retry) {
                    // if any requests get 401 while refresh-token request is pending, put them in the process queue
                    if (isFetchingRefreshToken) {
                        return new Promise(function (resolve, reject) {
                            failedRequestsQueue.push({resolve, reject});
                        })
                            .then((token) => {
                                originalRequest.headers["Authorization"] = "Bearer " + token;
                                return window.axios(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }
    
                    originalRequest._retry = true;
                    isFetchingRefreshToken = true;
                    /*
                     * We have to set config.withCredentials = true in order to send browser cookies within refresh-token request to FixitCoreApi which is on a different domain.
                     * This setting can be set globally on every axios request - if needed.
                     */
                    return new Promise(function (resolve, reject) {
                        window.axios
                            .post(
                                refreshTokenApiUrl,
                                null,
                                {withCredentials: true}
                            )
                            .then(({data}) => {
                                window.axios.defaults.headers.common["Authorization"] =
                                    "Bearer " + (data.jwtToken || data.accessToken || data.access_token);
                                originalRequest.headers["Authorization"] =
                                    "Bearer " + (data.jwtToken || data.accessToken || data.access_token);
                                processQueue(null, (data.jwtToken || data.accessToken || data.access_token));
                                resolve(window.axios(originalRequest));
                            })
                            .catch((err) => {
                                processQueue(err, null);
                                /*
                                 * If refresh token is expired redirect to login page
                                 */
                                if (logoutUrl !== "noredirect") {
                                    window.location.href = logoutUrl + "?returnUrl=" + window.location.href;
                                }
    
                                reject(err);
                            })
                            .then(() => {
                                isFetchingRefreshToken = false;
                            });
                    });
                }
    
                return Promise.reject(err);
            }
        );
    
        window.axios.interceptors.response.isRefreshTokenInterceptorSet = true;
    }
    
}
